import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const NegotiationScenarios = ({ data, onGenerateScenarios, loading = false }) => {
  const [selectedScenario, setSelectedScenario] = useState(0);
  const [selectedObjection, setSelectedObjection] = useState(0);

  if (loading || !data) {
    return (
      <div className="space-y-6">
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
              <Icon name="MessageSquare" size={16} className="text-primary" />
            </div>
            <h3 className="text-lg font-semibold text-foreground">Negotiation Scenarios</h3>
          </div>
          
          <div className="animate-pulse space-y-4">
            <div className="h-6 bg-muted rounded w-1/4" />
            <div className="space-y-3">
              <div className="h-4 bg-muted rounded" />
              <div className="h-4 bg-muted rounded w-3/4" />
              <div className="h-4 bg-muted rounded w-1/2" />
            </div>
          </div>
        </div>

        <div className="text-center">
          <p className="text-muted-foreground mb-4">Generate personalized negotiation scenarios</p>
          <div className="space-y-3">
            <Button
              variant="default"
              iconName="ArrowUp"
              iconPosition="left"
              onClick={() => onGenerateScenarios?.('promotion')}
              disabled={loading}
            >
              Generate Promotion Scenarios
            </Button>
            <Button
              variant="outline"
              iconName="DollarSign"
              iconPosition="left"
              onClick={() => onGenerateScenarios?.('raise')}
              disabled={loading}
            >
              Generate Salary Scenarios
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const openingScripts = data?.openingScripts || [];
  const objectionHandling = data?.objectionHandling || [];
  const strategy = data?.negotiationStrategy;
  const successPredictors = data?.successPredictors || [];

  return (
    <div className="space-y-6">
      {/* Opening Scripts */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
            <Icon name="MessageSquare" size={16} className="text-primary" />
          </div>
          <h3 className="text-lg font-semibold text-foreground">Opening Scripts</h3>
        </div>

        {openingScripts?.length > 0 && (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2 mb-4">
              {openingScripts?.map((script, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedScenario(index)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    selectedScenario === index
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  }`}
                >
                  {script?.scenario}
                </button>
              ))}
            </div>

            <div className="bg-muted/50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-foreground">
                  {openingScripts?.[selectedScenario]?.scenario}
                </h4>
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-muted-foreground">
                    {openingScripts?.[selectedScenario]?.tone}
                  </span>
                  <div className={`w-2 h-2 rounded-full ${
                    openingScripts?.[selectedScenario]?.effectiveness === 'High' ? 'bg-success' :
                    openingScripts?.[selectedScenario]?.effectiveness === 'Medium'? 'bg-warning' : 'bg-muted-foreground'
                  }`} />
                </div>
              </div>
              <div className="bg-card rounded-lg p-4 border-l-4 border-primary">
                <p className="text-sm text-foreground leading-relaxed">
                  "{openingScripts?.[selectedScenario]?.script}"
                </p>
              </div>
              <div className="mt-3 flex items-center space-x-2">
                <Icon name="Lightbulb" size={14} className="text-warning" />
                <span className="text-sm text-muted-foreground">
                  <strong>Effectiveness:</strong> {openingScripts?.[selectedScenario]?.effectiveness}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Objection Handling */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-8 h-8 bg-warning/10 rounded-full flex items-center justify-center">
            <Icon name="Shield" size={16} className="text-warning" />
          </div>
          <h3 className="text-lg font-semibold text-foreground">Common Objections & Responses</h3>
        </div>

        {objectionHandling?.length > 0 && (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2 mb-4">
              {objectionHandling?.map((obj, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedObjection(index)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    selectedObjection === index
                      ? 'bg-warning text-warning-foreground'
                      : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  }`}
                >
                  Objection {index + 1}
                </button>
              ))}
            </div>

            <div className="space-y-4">
              <div className="bg-destructive/10 rounded-lg p-4 border-l-4 border-destructive">
                <h4 className="font-medium text-foreground mb-2 flex items-center space-x-2">
                  <Icon name="AlertTriangle" size={16} className="text-destructive" />
                  <span>Common Objection</span>
                </h4>
                <p className="text-sm text-muted-foreground">
                  "{objectionHandling?.[selectedObjection]?.objection}"
                </p>
              </div>

              <div className="bg-success/10 rounded-lg p-4 border-l-4 border-success">
                <h4 className="font-medium text-foreground mb-2 flex items-center space-x-2">
                  <Icon name="CheckCircle" size={16} className="text-success" />
                  <span>Recommended Response</span>
                </h4>
                <p className="text-sm text-muted-foreground">
                  "{objectionHandling?.[selectedObjection]?.response}"
                </p>
              </div>

              <div className="bg-primary/10 rounded-lg p-4 border-l-4 border-primary">
                <h4 className="font-medium text-foreground mb-2 flex items-center space-x-2">
                  <Icon name="ArrowRight" size={16} className="text-primary" />
                  <span>Follow-up Strategy</span>
                </h4>
                <p className="text-sm text-muted-foreground">
                  {objectionHandling?.[selectedObjection]?.followUp}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Negotiation Strategy */}
      {strategy && (
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-8 h-8 bg-secondary/10 rounded-full flex items-center justify-center">
              <Icon name="Target" size={16} className="text-secondary" />
            </div>
            <h3 className="text-lg font-semibold text-foreground">Strategic Approach</h3>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="bg-muted/50 rounded-lg p-4">
                <h4 className="font-medium text-foreground mb-3 flex items-center space-x-2">
                  <Icon name="List" size={16} className="text-primary" />
                  <span>Preparation Steps</span>
                </h4>
                <div className="space-y-2">
                  {strategy?.preparation?.map((step, index) => (
                    <div key={index} className="flex items-start space-x-2">
                      <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0" />
                      <p className="text-sm text-muted-foreground">{step}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-muted/50 rounded-lg p-4">
                <h4 className="font-medium text-foreground mb-2 flex items-center space-x-2">
                  <Icon name="Clock" size={16} className="text-warning" />
                  <span>Optimal Timing</span>
                </h4>
                <p className="text-sm text-muted-foreground">
                  {strategy?.timing}
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="bg-muted/50 rounded-lg p-4">
                <h4 className="font-medium text-foreground mb-3 flex items-center space-x-2">
                  <Icon name="GitBranch" size={16} className="text-secondary" />
                  <span>Alternative Options</span>
                </h4>
                <div className="space-y-2">
                  {strategy?.alternatives?.map((alternative, index) => (
                    <div key={index} className="flex items-start space-x-2">
                      <div className="w-1.5 h-1.5 bg-secondary rounded-full mt-2 flex-shrink-0" />
                      <p className="text-sm text-muted-foreground">{alternative}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-muted/50 rounded-lg p-4">
                <h4 className="font-medium text-foreground mb-2 flex items-center space-x-2">
                  <Icon name="X" size={16} className="text-destructive" />
                  <span>Walk-away Point</span>
                </h4>
                <p className="text-sm text-muted-foreground">
                  {strategy?.walkAwayPoint}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Predictors */}
      {successPredictors?.length > 0 && (
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-8 h-8 bg-success/10 rounded-full flex items-center justify-center">
              <Icon name="TrendingUp" size={16} className="text-success" />
            </div>
            <h3 className="text-lg font-semibold text-foreground">Success Factors</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {successPredictors?.map((predictor, index) => (
              <div key={index} className="bg-muted/50 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <div className={`w-2 h-2 rounded-full ${
                    predictor?.importance === 'high' ? 'bg-success' :
                    predictor?.importance === 'medium'? 'bg-warning' : 'bg-muted-foreground'
                  }`} />
                  <h4 className="font-medium text-foreground">{predictor?.factor}</h4>
                </div>
                <p className="text-sm text-muted-foreground">
                  {predictor?.description}
                </p>
                <span className={`text-xs px-2 py-1 rounded-full mt-2 inline-block ${
                  predictor?.importance === 'high' ? 'bg-success/20 text-success' :
                  predictor?.importance === 'medium'? 'bg-warning/20 text-warning' : 'bg-muted text-muted-foreground'
                }`}>
                  {predictor?.importance} priority
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default NegotiationScenarios;